import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiArrowLeft,
  FiMapPin,
  FiCalendar,
  FiClock,
  FiUsers,
  FiGlobe,
  FiMonitor,
  FiStar,
  FiCheck,
  FiShare2,
  FiBookmark,
  FiCreditCard,
  FiInfo,
  FiMessageCircle,
  FiBook,
  FiLayout,
  FiMail,
  FiTag,
  FiZap,
  FiHash,
  FiChevronRight,
  FiExternalLink,
  FiAward,
  FiBell,
  FiCopy,
  FiCheckCircle,
  FiChevronDown,
  FiTarget,
  FiList,
  FiBarChart2,
  FiPhone,
  FiLoader,
  FiLogOut,
  FiSettings,
  FiAlertCircle,
  FiEdit2,
  FiPlus,
  FiX,
} from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import { workshopApi } from "../../services/workshopApi";
import { useUserContext } from "../../context/UserContext";
import { getDaysUntil, formatPrice } from "../../utils/workshopUtils";

// Existing Tab Components
import DiscussionForum from "./DiscussionForum";
import ResourceLibrary from "./ResourceLibrary";
import ParticipantManagement from "./ParticipantManagement";
import ParticipantList from "./ParticipantList";
import CoOrganizerManagement from "./CoOrganizerManagement";
import Announcements from "./Announcements";

import "./WorkshopDetail.css";

// =========================================
// ANIMATION VARIANTS
// =========================================
const pageVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.4, staggerChildren: 0.1 } },
  exit: { opacity: 0, transition: { duration: 0.2 } },
};

const itemVariants = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] } },
};

const cardVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
  hover: { y: -2, transition: { duration: 0.2 } },
};

const tabContentVariants = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, x: -20, transition: { duration: 0.2 } },
};

// =========================================
// SUB-COMPONENTS
// =========================================

// Compact Header Component - Ultra Minimal
const CompactHeader = ({ workshop, userRole, onApply, onSave, onShare, onBack, isJoining = false }) => {
  const daysUntil = getDaysUntil(workshop.duration?.startDate);
  const isFree = workshop.pricing?.type === "free";
  const isOnline = workshop.format === "online";
  const [copied, setCopied] = useState(false);

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const renderActionButton = () => {
    if (userRole === "organizer" || userRole === "co-organizer") {
      return null;
    }

    // Show loading state when joining
    if (isJoining) {
      return (
        <motion.button className="wkd-action-btn wkd-btn-loading" disabled>
          <FiLoader className="wkd-spinner" /> Joining...
        </motion.button>
      );
    }

    if (workshop.userRole?.isParticipant) {
      const status = workshop.userRole.participantStatus;

      if (status === "approved") {
        return (
          <motion.button whileHover={{ scale: 1.02 }} className="wkd-action-btn wkd-btn-enrolled" disabled>
            <FiCheckCircle /> Enrolled
          </motion.button>
        );
      }

      if (status === "pending") {
        return (
          <motion.button className="wkd-action-btn wkd-btn-pending" disabled>
            <FiClock /> Pending
          </motion.button>
        );
      }

      if (status === "payment_pending") {
        return (
          <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={onApply} className="wkd-action-btn wkd-btn-primary">
            <FiCreditCard /> Pay
          </motion.button>
        );
      }

      if (status === "rejected" && workshop.userRole?.canReapply) {
        return (
          <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={onApply} className="wkd-action-btn wkd-btn-primary">
            Reapply
          </motion.button>
        );
      }
    }

    if (workshop.userRole?.shouldShowApplyButton) {
      return (
        <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={onApply} className="wkd-action-btn wkd-btn-primary">
          <FiZap /> Apply
        </motion.button>
      );
    }

    return null;
  };

  return (
    <motion.header className="wkd-header" variants={itemVariants}>
      <div className="wkd-header-row">
        {/* Left: Back + Title */}
        <div className="wkd-header-left">
          <motion.button
            whileHover={{ x: -2 }}
            whileTap={{ scale: 0.95 }}
            onClick={onBack}
            className="wkd-back-btn"
          >
            <FiArrowLeft />
          </motion.button>
          <h1 className="wkd-title">{workshop.title}</h1>
        </div>

        {/* Center: Badges */}
        <div className="wkd-badges">
          {daysUntil > 0 && daysUntil <= 7 && (
            <span className="wkd-badge wkd-badge-soon"><FiClock /> Soon</span>
          )}
          {isFree ? (
            <span className="wkd-badge wkd-badge-free">Free</span>
          ) : (
            <span className="wkd-badge wkd-badge-paid">
              {formatPrice(workshop.pricing?.amount || 0, workshop.pricing?.currency || "PKR")}
            </span>
          )}
          <span className={`wkd-badge ${isOnline ? "wkd-badge-online" : "wkd-badge-physical"}`}>
            {isOnline ? <><FiMonitor /> Online</> : <><FiMapPin /> Physical</>}
          </span>
        </div>

        {/* Right: Actions */}
        <div className="wkd-actions">
          {renderActionButton()}
          <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={onSave} className="wkd-icon-btn" title="Save">
            <FiBookmark />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleShare}
            className={`wkd-icon-btn ${copied ? "wkd-copied" : ""}`}
            title={copied ? "Copied!" : "Share"}
          >
            {copied ? <FiCheck /> : <FiShare2 />}
          </motion.button>
        </div>
      </div>
    </motion.header>
  );
};

// Modern Tabs Component
const TabsNavigation = ({ tabs, activeTab, onTabChange }) => {
  const tabIcons = {
    overview: FiLayout,
    schedule: FiCalendar,
    announcements: FiBell,
    resources: FiBook,
    discussions: FiMessageCircle,
    participants: FiUsers,
  };

  return (
    <div className="wkd-tabs-wrapper">
      <nav className="wkd-tabs">
        {tabs.map((tab) => {
          const Icon = tabIcons[tab.id] || FiLayout;
          const isActive = activeTab === tab.id;
          return (
            <motion.button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`wkd-tab ${isActive ? "wkd-tab-active" : ""}`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Icon />
              <span>{tab.label}</span>
              {isActive && (
                <motion.div className="wkd-tab-indicator" layoutId="tabIndicator" transition={{ duration: 0.3, ease: "easeOut" }} />
              )}
            </motion.button>
          );
        })}
      </nav>
    </div>
  );
};

// Announcement Preview Card for Overview section
const AnnouncementPreviewCard = ({ announcement }) => {
  const [expanded, setExpanded] = useState(false);
  const contentLength = announcement.content?.length || 0;
  const shouldTruncate = contentLength > 100;

  return (
    <div className="wkd-announcement">
      <div className={`wkd-announcement-priority wkd-priority-${announcement.priority || "normal"}`} />
      <div className="wkd-announcement-content">
        <h4>{announcement.title}</h4>
        <p>
          {expanded || !shouldTruncate
            ? announcement.content
            : `${announcement.content?.substring(0, 100)}...`}
        </p>
        {shouldTruncate && (
          <button className="wkd-see-more-btn wkd-see-more-inline" onClick={() => setExpanded(!expanded)}>
            {expanded ? "See less" : "See more"}
          </button>
        )}
        <span className="wkd-announcement-meta">
          {announcement.author?.name} • {new Date(announcement.createdAt).toLocaleDateString()}
        </span>
      </div>
    </div>
  );
};

// Overview Content with all details
const OverviewContent = ({ workshop, isOrganizer = false }) => {
  const navigate = useNavigate();
  const daysUntil = getDaysUntil(workshop.duration?.startDate);
  const isPaid = workshop.pricing?.type === "paid";
  const isOnline = workshop.format === "online";
  const [showPayment, setShowPayment] = useState(false);
  const [joinCodeCopied, setJoinCodeCopied] = useState(false);

  // Copy join code handler
  const handleCopyJoinCode = () => {
    if (workshop.joinCode) {
      navigator.clipboard.writeText(workshop.joinCode);
      setJoinCodeCopied(true);
      setTimeout(() => setJoinCodeCopied(false), 2000);
    }
  };

  const handleCopyShareText = () => {
    const shareText = `Join my workshop "${workshop.title}" using code: ${workshop.joinCode}`;
    navigator.clipboard.writeText(shareText);
    setJoinCodeCopied(true);
    setTimeout(() => setJoinCodeCopied(false), 2000);
  };

  // Helper: Format 24h time to 12h format
  const formatTime24to12 = (time24) => {
    if (!time24) return "";
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes?.toString().padStart(2, '0') || '00'} ${period}`;
  };

  // Get workshop time range from schedule (correct way)
  const getWorkshopTimeRange = () => {
    if (!workshop.schedule || workshop.schedule.length === 0) {
      return "Time TBA";
    }
    
    let earliestStart = "23:59";
    let latestEnd = "00:00";
    
    workshop.schedule.forEach(day => {
      day.sessions?.forEach(session => {
        if (session.startTime && session.startTime < earliestStart) {
          earliestStart = session.startTime;
        }
        if (session.endTime && session.endTime > latestEnd) {
          latestEnd = session.endTime;
        }
      });
    });
    
    if (earliestStart === "23:59" || latestEnd === "00:00") {
      return "Time TBA";
    }
    
    return `${formatTime24to12(earliestStart)} - ${formatTime24to12(latestEnd)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  // Difficulty level config
  const difficultyConfig = {
    "beginner": { label: "Beginner", color: "green", icon: "🌱", description: "No prior experience needed" },
    "intermediate": { label: "Intermediate", color: "yellow", icon: "📈", description: "Some experience recommended" },
    "advanced": { label: "Advanced", color: "red", icon: "🚀", description: "Strong foundation required" },
    "all-levels": { label: "All Levels", color: "blue", icon: "👥", description: "Suitable for everyone" },
  };

  // Calculate capacity percentage
  const capacityLimit = workshop.capacity?.unlimited ? 100 : (workshop.capacity?.limit || 50);
  const participantCount = workshop.participantCount || 0;
  const capacityPercentage = workshop.capacity?.unlimited ? 0 : Math.min((participantCount / capacityLimit) * 100, 100);
  const isAlmostFull = capacityPercentage >= 80;
  const isFull = capacityPercentage >= 100;

  return (
    <motion.div className="wkd-overview" variants={tabContentVariants} initial="initial" animate="animate" exit="exit">
      {/* Join Code Section - Only for Organizers */}
      {isOrganizer && workshop.joinCode && (
        <motion.section className="wkd-section wkd-join-code-section" variants={cardVariants}>
          <h3 className="wkd-section-title">
            <FiShare2 /> Invite Code
          </h3>
          <div className="wkd-join-code-container">
            <div className="wkd-join-code-display">
              <span className="wkd-join-code-label">Share this code with participants:</span>
              <div className="wkd-join-code-box">
                <code className="wkd-join-code-value">{workshop.joinCode}</code>
                <button 
                  className={`wkd-copy-btn ${joinCodeCopied ? 'wkd-copied' : ''}`}
                  onClick={handleCopyJoinCode}
                  title="Copy code"
                >
                  {joinCodeCopied ? <FiCheck /> : <FiCopy />}
                </button>
              </div>
            </div>
            <button 
              className="wkd-share-text-btn"
              onClick={handleCopyShareText}
            >
              <FiCopy /> Copy Share Message
            </button>
          </div>
        </motion.section>
      )}

      {/* Hero Info Section - Organizer + Key Details */}
      <div className="wkd-hero-info">
        {/* Organizer */}
        <motion.div
          className="wkd-organizer-block"
          whileHover={{ scale: 1.01 }}
          onClick={() => navigate(`/profile/${workshop.organizer?._id || workshop.organizer?.id}`)}
        >
          <div className="wkd-organizer-avatar-lg">
            {workshop.organizer?.profilePhotoUrl ? (
              <img src={workshop.organizer.profilePhotoUrl} alt={workshop.organizer.name} />
            ) : (
              <span>{workshop.organizer?.name?.charAt(0)?.toUpperCase() || "?"}</span>
            )}
          </div>
          <div className="wkd-organizer-text">
            <span className="wkd-organizer-label">Organizer</span>
            <span className="wkd-organizer-name-lg">
              {workshop.organizer?.name}
              {workshop.organizer?.isVerified && <FiCheck className="wkd-verified" />}
            </span>
            {workshop.organizer?.professionalRole && (
              <span className="wkd-organizer-role-lg">{workshop.organizer.professionalRole}</span>
            )}
          </div>
          <FiChevronRight className="wkd-organizer-arrow" />
        </motion.div>

        {/* Key Details Grid - Compact Cards */}
        <div className="wkd-details-grid">
          <div className="wkd-detail-card">
            <div className="wkd-detail-card-icon wkd-icon-blue">
              <FiCalendar />
            </div>
            <div className="wkd-detail-card-content">
              <span className="wkd-detail-card-label">Date</span>
              <span className="wkd-detail-card-value">
                {formatDate(workshop.duration?.startDate)} - {formatDate(workshop.duration?.endDate)}
              </span>
            </div>
          </div>

          <div className="wkd-detail-card">
            <div className="wkd-detail-card-icon wkd-icon-indigo">
              <FiClock />
            </div>
            <div className="wkd-detail-card-content">
              <span className="wkd-detail-card-label">Time</span>
              <span className="wkd-detail-card-value">{getWorkshopTimeRange()}</span>
            </div>
          </div>

          <div className="wkd-detail-card">
            <div className="wkd-detail-card-icon wkd-icon-cyan">
              <FiMapPin />
            </div>
            <div className="wkd-detail-card-content">
              <span className="wkd-detail-card-label">Location</span>
              <span className="wkd-detail-card-value">
                {isOnline ? workshop.location?.platform || "Online" : `${workshop.location?.city || ""}${workshop.location?.country ? `, ${workshop.location.country}` : ""}`}
              </span>
            </div>
          </div>

          {/* Capacity Card with Progress Bar */}
          <div className={`wkd-detail-card wkd-capacity-card ${isFull ? 'wkd-capacity-full' : isAlmostFull ? 'wkd-capacity-warning' : ''}`}>
            <div className={`wkd-detail-card-icon ${isFull ? 'wkd-icon-red' : isAlmostFull ? 'wkd-icon-amber' : 'wkd-icon-green'}`}>
              <FiUsers />
            </div>
            <div className="wkd-detail-card-content wkd-capacity-content">
              <div className="wkd-capacity-header">
                <span className="wkd-detail-card-label">Capacity</span>
                <span className="wkd-capacity-count">
                  <span className="wkd-capacity-current">{participantCount}</span>
                  <span className="wkd-capacity-separator">/</span>
                  <span className="wkd-capacity-max">{workshop.capacity?.unlimited ? "∞" : capacityLimit}</span>
                </span>
              </div>
              {!workshop.capacity?.unlimited && (
                <div className="wkd-capacity-bar-wrapper">
                  <motion.div 
                    className={`wkd-capacity-bar-fill ${isFull ? 'wkd-bar-full' : isAlmostFull ? 'wkd-bar-warning' : 'wkd-bar-normal'}`}
                    initial={{ width: 0 }}
                    animate={{ width: `${capacityPercentage}%` }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                  />
                </div>
              )}
              <span className="wkd-capacity-status">
                {isFull ? "Full" : isAlmostFull ? "Almost Full" : `${Math.round(capacityPercentage)}% filled`}
              </span>
            </div>
          </div>

          <div className="wkd-detail-card">
            <div className="wkd-detail-card-icon wkd-icon-pink">
              <FiGlobe />
            </div>
            <div className="wkd-detail-card-content">
              <span className="wkd-detail-card-label">Language</span>
              <span className="wkd-detail-card-value">{workshop.language || "English"}</span>
            </div>
          </div>

          {workshop.category && (
            <div className="wkd-detail-card">
              <div className="wkd-detail-card-icon wkd-icon-indigo">
                <FiBook />
              </div>
              <div className="wkd-detail-card-content">
                <span className="wkd-detail-card-label">Category</span>
                <span className="wkd-detail-card-value">{workshop.category}</span>
              </div>
            </div>
          )}

          {/* Field - NEW */}
          {workshop.field && (
            <div className="wkd-detail-card">
              <div className="wkd-detail-card-icon wkd-icon-teal">
                <FiTarget />
              </div>
              <div className="wkd-detail-card-content">
                <span className="wkd-detail-card-label">Field</span>
                <span className="wkd-detail-card-value">{workshop.field}</span>
              </div>
            </div>
          )}

          {/* Difficulty Level - NEW */}
          {workshop.difficultyLevel && (
            <div className="wkd-detail-card">
              <div className={`wkd-detail-card-icon wkd-icon-${difficultyConfig[workshop.difficultyLevel]?.color || 'blue'}`}>
                <FiBarChart2 />
              </div>
              <div className="wkd-detail-card-content">
                <span className="wkd-detail-card-label">Level</span>
                <span className={`wkd-detail-card-value wkd-difficulty-badge wkd-difficulty-${workshop.difficultyLevel}`}>
                  {difficultyConfig[workshop.difficultyLevel]?.icon} {difficultyConfig[workshop.difficultyLevel]?.label || workshop.difficultyLevel}
                </span>
              </div>
            </div>
          )}

          {/* Certificate - NEW */}
          {workshop.certificateOffered && (
            <div className="wkd-detail-card wkd-certificate-card">
              <div className="wkd-detail-card-icon wkd-icon-gold">
                <FiAward />
              </div>
              <div className="wkd-detail-card-content">
                <span className="wkd-detail-card-label">Certificate</span>
                <span className="wkd-detail-card-value wkd-certificate-badge">
                  ✅ Provided
                </span>
              </div>
            </div>
          )}

          {daysUntil > 0 && (
            <div className="wkd-detail-card wkd-countdown-card">
              <div className="wkd-detail-card-icon wkd-icon-amber">
                <FiZap />
              </div>
              <div className="wkd-detail-card-content">
                <span className="wkd-detail-card-label">Starts In</span>
                <span className="wkd-detail-card-value wkd-countdown-value">
                  <span className="wkd-countdown-number">{daysUntil}</span> day{daysUntil !== 1 ? "s" : ""}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* About Section */}
      <motion.section className="wkd-section" variants={cardVariants}>
        <h3 className="wkd-section-title">
          <FiInfo /> About this Workshop
        </h3>
        <p className="wkd-section-text">{workshop.description}</p>
      </motion.section>

      {/* What You'll Learn - NEW */}
      {workshop.learningOutcomes && workshop.learningOutcomes.length > 0 && (
        <motion.section className="wkd-section wkd-learning-section" variants={cardVariants}>
          <h3 className="wkd-section-title">
            <FiCheckCircle /> What You'll Learn
          </h3>
          <ul className="wkd-learning-list">
            {workshop.learningOutcomes.map((outcome, index) => (
              <motion.li
                key={index}
                className="wkd-learning-item"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <FiCheck className="wkd-learning-check" />
                <span>{outcome}</span>
              </motion.li>
            ))}
          </ul>
        </motion.section>
      )}

      {/* Prerequisites - NEW */}
      {workshop.prerequisites && workshop.prerequisites.length > 0 && (
        <motion.section className="wkd-section wkd-prereq-section" variants={cardVariants}>
          <h3 className="wkd-section-title">
            <FiList /> Prerequisites
          </h3>
          <ul className="wkd-prereq-list">
            {workshop.prerequisites.map((prereq, index) => (
              <motion.li
                key={index}
                className="wkd-prereq-item"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <span className="wkd-prereq-bullet">•</span>
                <span>{prereq}</span>
              </motion.li>
            ))}
          </ul>
        </motion.section>
      )}

      {/* Who Should Attend - NEW */}
      {workshop.targetAudience && (
        <motion.section className="wkd-section" variants={cardVariants}>
          <h3 className="wkd-section-title">
            <FiUsers /> Who Should Attend
          </h3>
          <p className="wkd-section-text">{workshop.targetAudience}</p>
        </motion.section>
      )}

      {/* Workshop Features - NEW */}
      {(workshop.publicSettings || workshop.certificateOffered || workshop.isFeatured) && (
        <motion.section className="wkd-section wkd-features-section" variants={cardVariants}>
          <h3 className="wkd-section-title">
            <FiZap /> Workshop Features
          </h3>
          <div className="wkd-features-grid">
            {workshop.publicSettings?.allowsQuestions !== undefined && (
              <div className={`wkd-feature-item ${workshop.publicSettings.allowsQuestions ? 'wkd-feature-active' : ''}`}>
                <FiMessageCircle />
                <span>{workshop.publicSettings.allowsQuestions ? '✅ Q&A Sessions Enabled' : '❌ Q&A Disabled'}</span>
              </div>
            )}
            {workshop.publicSettings?.requiresApproval !== undefined && (
              <div className="wkd-feature-item">
                {workshop.publicSettings.requiresApproval ? (
                  <>
                    <FiCheck />
                    <span>🔒 Approval Required</span>
                  </>
                ) : (
                  <>
                    <FiZap />
                    <span>⚡ Instant Join</span>
                  </>
                )}
              </div>
            )}
            {workshop.certificateOffered && (
              <div className="wkd-feature-item wkd-feature-active">
                <FiAward />
                <span>🏆 Certificate on Completion</span>
              </div>
            )}
            {workshop.isFeatured && (
              <div className="wkd-feature-item wkd-feature-featured">
                <FiStar />
                <span>⭐ Featured Workshop</span>
              </div>
            )}
          </div>
          {workshop.certificateDetails && (
            <p className="wkd-certificate-details">{workshop.certificateDetails}</p>
          )}
        </motion.section>
      )}

      {/* Tags */}
      {workshop.tags && workshop.tags.length > 0 && (
        <motion.section className="wkd-section" variants={cardVariants}>
          <h3 className="wkd-section-title">
            <FiTag /> Tags
          </h3>
          <div className="wkd-tags">
            {workshop.tags.map((tag, index) => (
              <motion.span
                key={index}
                className="wkd-tag"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.05 }}
              >
                <FiHash /> {tag}
              </motion.span>
            ))}
          </div>
        </motion.section>
      )}

      {/* Requirements (legacy field - keep for backwards compatibility) */}
      {workshop.requirements && (
        <motion.section className="wkd-section" variants={cardVariants}>
          <h3 className="wkd-section-title">
            <FiAward /> Requirements
          </h3>
          <p className="wkd-section-text">{workshop.requirements}</p>
        </motion.section>
      )}

      {/* Co-organizers */}
      {workshop.coOrganizers && workshop.coOrganizers.length > 0 && (
        <motion.section className="wkd-section" variants={cardVariants}>
          <h3 className="wkd-section-title">
            <FiUsers /> Co-organizers ({workshop.coOrganizers.length})
          </h3>
          <div className="wkd-co-organizers">
            {workshop.coOrganizers.map((co, index) => {
              // Build permissions list
              const permissionLabels = [];
              if (co.permissions?.canApproveParticipants) permissionLabels.push('Approve');
              if (co.permissions?.canUploadResources) permissionLabels.push('Resources');
              if (co.permissions?.canSendAnnouncements) permissionLabels.push('Announce');
              if (co.permissions?.canManageSchedule) permissionLabels.push('Schedule');
              
              return (
                <motion.div
                  key={co.user?._id || co._id || index}
                  className="wkd-co-organizer"
                  onClick={() => navigate(`/profile/${co.user?._id || co.user?.id}`)}
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="wkd-co-avatar">
                    {co.user?.profilePhotoUrl ? (
                      <img src={co.user.profilePhotoUrl} alt={co.user?.name} />
                    ) : (
                      <span>{co.user?.name?.charAt(0)?.toUpperCase() || "?"}</span>
                    )}
                  </div>
                  <div className="wkd-co-info">
                    <span className="wkd-co-name">{co.user?.name || "Unknown"}</span>
                    {permissionLabels.length > 0 && (
                      <span className="wkd-co-permissions">
                        Can: {permissionLabels.join(', ')}
                      </span>
                    )}
                  </div>
                  <FiExternalLink className="wkd-co-link" />
                </motion.div>
              );
            })}
          </div>
        </motion.section>
      )}

      {/* Payment Info for paid workshops - check both locations for payment instructions */}
      {isPaid && (workshop.paymentInstructions || workshop.pricing?.paymentInstructions) && (
        <motion.section className="wkd-section wkd-payment-section" variants={cardVariants}>
          <button className="wkd-payment-toggle" onClick={() => setShowPayment(!showPayment)}>
            <div className="wkd-payment-toggle-left">
              <FiCreditCard />
              <span>Payment Instructions</span>
            </div>
            <motion.div animate={{ rotate: showPayment ? 180 : 0 }} transition={{ duration: 0.2 }}>
              <FiChevronDown />
            </motion.div>
          </button>
          <AnimatePresence>
            {showPayment && (
              <motion.div
                className="wkd-payment-content"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <PaymentDetails paymentInstructions={workshop.paymentInstructions || workshop.pricing.paymentInstructions} />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.section>
      )}

      {/* Announcements Preview */}
      {workshop.announcements && workshop.announcements.length > 0 && (
        <motion.section className="wkd-section" variants={cardVariants}>
          <h3 className="wkd-section-title">
            <FiBell /> Latest Announcements
          </h3>
          <div className="wkd-announcements">
            {workshop.announcements.slice(0, 2).map((announcement) => (
              <AnnouncementPreviewCard key={announcement._id} announcement={announcement} />
            ))}
          </div>
        </motion.section>
      )}
    </motion.div>
  );
};

// Payment Details Sub-component
const PaymentDetails = ({ paymentInstructions }) => {
  const { bankDetails, mobilePayment, internationalPayment, additionalInstructions, contactInfo } = paymentInstructions;
  const [copiedField, setCopiedField] = useState(null);

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Format WhatsApp number for wa.me link
  const formatWhatsAppNumber = (number) => {
    if (!number) return '';
    // Remove spaces, dashes, and non-numeric except +
    return number.replace(/[^\d+]/g, '').replace(/^0/, '92'); // Pakistan: 03xxx -> 923xxx
  };

  const PaymentMethod = ({ title, icon, details }) => (
    <div className="wkd-payment-method">
      <div className="wkd-payment-method-header">
        {icon}
        <span>{title}</span>
      </div>
      <div className="wkd-payment-method-details">
        {details.map((detail, idx) => (
          <div key={idx} className="wkd-payment-detail">
            <span className="wkd-payment-label">{detail.label}</span>
            <div className="wkd-payment-value-row">
              <span className="wkd-payment-value">{detail.value}</span>
              <button
                className={`wkd-copy-btn ${copiedField === `${title}-${idx}` ? "wkd-copied" : ""}`}
                onClick={() => copyToClipboard(detail.value, `${title}-${idx}`)}
              >
                {copiedField === `${title}-${idx}` ? <FiCheck /> : <FiCopy />}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="wkd-payment-details">
      {bankDetails?.accountNumber && (
        <PaymentMethod
          title="Bank Transfer"
          icon={<FiCreditCard />}
          details={[
            bankDetails.bankName && { label: "Bank", value: bankDetails.bankName },
            bankDetails.accountTitle && { label: "Account Title", value: bankDetails.accountTitle },
            { label: "Account Number", value: bankDetails.accountNumber },
            bankDetails.iban && { label: "IBAN", value: bankDetails.iban },
            bankDetails.branchCode && { label: "Branch Code", value: bankDetails.branchCode },
          ].filter(Boolean)}
        />
      )}
      {mobilePayment?.easypaisa?.accountNumber && (
        <PaymentMethod
          title="EasyPaisa"
          icon={<FiPhone />}
          details={[
            { label: "Account", value: mobilePayment.easypaisa.accountNumber },
            mobilePayment.easypaisa.accountTitle && { label: "Title", value: mobilePayment.easypaisa.accountTitle },
          ].filter(Boolean)}
        />
      )}
      {mobilePayment?.jazzcash?.accountNumber && (
        <PaymentMethod
          title="JazzCash"
          icon={<FiPhone />}
          details={[
            { label: "Account", value: mobilePayment.jazzcash.accountNumber },
            mobilePayment.jazzcash.accountTitle && { label: "Title", value: mobilePayment.jazzcash.accountTitle },
          ].filter(Boolean)}
        />
      )}
      {internationalPayment?.paypal?.email && (
        <PaymentMethod
          title="PayPal"
          icon={<FiGlobe />}
          details={[{ label: "Email", value: internationalPayment.paypal.email }]}
        />
      )}
      {internationalPayment?.wise?.email && (
        <PaymentMethod
          title="Wise"
          icon={<FiGlobe />}
          details={[{ label: "Email", value: internationalPayment.wise.email }]}
        />
      )}
      {additionalInstructions && (
        <div className="wkd-additional-instructions">
          <h5>Additional Instructions</h5>
          <p>{additionalInstructions}</p>
        </div>
      )}
      
      {/* Contact Info for Payment Queries - NEW */}
      {contactInfo && (contactInfo.whatsapp || contactInfo.email) && (
        <div className="wkd-payment-contact">
          <h5>📞 Contact for Payment Queries</h5>
          <div className="wkd-contact-methods">
            {contactInfo.whatsapp && (
              <a
                href={`https://wa.me/${formatWhatsAppNumber(contactInfo.whatsapp)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="wkd-contact-link wkd-contact-whatsapp"
              >
                <FaWhatsapp />
                <span>{contactInfo.whatsapp}</span>
                <button
                  className={`wkd-copy-btn ${copiedField === 'whatsapp' ? "wkd-copied" : ""}`}
                  onClick={(e) => {
                    e.preventDefault();
                    copyToClipboard(contactInfo.whatsapp, 'whatsapp');
                  }}
                >
                  {copiedField === 'whatsapp' ? <FiCheck /> : <FiCopy />}
                </button>
              </a>
            )}
            {contactInfo.email && (
              <a
                href={`mailto:${contactInfo.email}`}
                className="wkd-contact-link wkd-contact-email"
              >
                <FiMail />
                <span>{contactInfo.email}</span>
                <button
                  className={`wkd-copy-btn ${copiedField === 'email' ? "wkd-copied" : ""}`}
                  onClick={(e) => {
                    e.preventDefault();
                    copyToClipboard(contactInfo.email, 'email');
                  }}
                >
                  {copiedField === 'email' ? <FiCheck /> : <FiCopy />}
                </button>
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Schedule Content Component
const ScheduleContent = ({ workshop, workshopId, canEdit, onScheduleUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  const handleEditClick = () => {
    // Initialize editing with current schedule data
    setEditingSchedule(workshop.schedule ? JSON.parse(JSON.stringify(workshop.schedule)) : []);
    setIsEditing(true);
  };

  const handleSaveSchedule = async () => {
    try {
      setIsSaving(true);
      await workshopApi.updateSchedule(workshopId, editingSchedule);
      setIsEditing(false);
      if (onScheduleUpdate) {
        onScheduleUpdate();
      }
    } catch (error) {
alert(error.message || "Failed to save schedule. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSessionChange = (dayIndex, sessionIndex, field, value) => {
    const newSchedule = [...editingSchedule];
    if (!newSchedule[dayIndex].sessions) {
      newSchedule[dayIndex].sessions = [];
    }
    newSchedule[dayIndex].sessions[sessionIndex] = {
      ...newSchedule[dayIndex].sessions[sessionIndex],
      [field]: value
    };
    setEditingSchedule(newSchedule);
  };

  const handleAddSession = (dayIndex) => {
    const newSchedule = [...editingSchedule];
    if (!newSchedule[dayIndex].sessions) {
      newSchedule[dayIndex].sessions = [];
    }
    newSchedule[dayIndex].sessions.push({
      startTime: "09:00",
      endTime: "10:00",
      topic: "",
      agenda: ""
    });
    setEditingSchedule(newSchedule);
  };

  const handleRemoveSession = (dayIndex, sessionIndex) => {
    const newSchedule = [...editingSchedule];
    newSchedule[dayIndex].sessions.splice(sessionIndex, 1);
    setEditingSchedule(newSchedule);
  };

  if (!workshop.schedule || workshop.schedule.length === 0) {
    return (
      <motion.div className="wkd-empty-state" variants={tabContentVariants} initial="initial" animate="animate" exit="exit">
        <div className="wkd-empty-icon">
          <FiCalendar />
        </div>
        <h3>No schedule yet</h3>
        <p>The organizer hasn't added a schedule for this workshop.</p>
        {canEdit && (
          <button className="wkd-add-schedule-btn" onClick={handleEditClick}>
            <FiPlus /> Add Schedule
          </button>
        )}
      </motion.div>
    );
  }

  // Edit Mode
  if (isEditing) {
    return (
      <motion.div className="wkd-schedule wkd-schedule-editing" variants={tabContentVariants} initial="initial" animate="animate" exit="exit">
        <div className="wkd-schedule-edit-header">
          <h3>Edit Schedule</h3>
          <div className="wkd-schedule-edit-actions">
            <button 
              className="wkd-btn wkd-btn-secondary" 
              onClick={() => setIsEditing(false)}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button 
              className="wkd-btn wkd-btn-primary" 
              onClick={handleSaveSchedule}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save Schedule"}
            </button>
          </div>
        </div>
        
        {editingSchedule.map((day, dayIndex) => (
          <div key={dayIndex} className="wkd-schedule-day wkd-schedule-day-editing">
            <div className="wkd-day-header">
              <span className="wkd-day-badge">Day {day.day}</span>
              <span className="wkd-day-date">{formatDate(day.date)}</span>
            </div>
            <div className="wkd-sessions">
              {day.sessions?.map((session, sessionIndex) => (
                <div key={sessionIndex} className="wkd-session wkd-session-editing">
                  <div className="wkd-session-edit-row">
                    <input
                      type="time"
                      value={session.startTime || "09:00"}
                      onChange={(e) => handleSessionChange(dayIndex, sessionIndex, 'startTime', e.target.value)}
                      className="wkd-time-input"
                    />
                    <span>-</span>
                    <input
                      type="time"
                      value={session.endTime || "10:00"}
                      onChange={(e) => handleSessionChange(dayIndex, sessionIndex, 'endTime', e.target.value)}
                      className="wkd-time-input"
                    />
                    <button 
                      className="wkd-remove-session-btn"
                      onClick={() => handleRemoveSession(dayIndex, sessionIndex)}
                      title="Remove session"
                    >
                      <FiX />
                    </button>
                  </div>
                  <input
                    type="text"
                    value={session.topic || ""}
                    onChange={(e) => handleSessionChange(dayIndex, sessionIndex, 'topic', e.target.value)}
                    placeholder="Session topic..."
                    className="wkd-topic-input"
                  />
                  <textarea
                    value={session.agenda || ""}
                    onChange={(e) => handleSessionChange(dayIndex, sessionIndex, 'agenda', e.target.value)}
                    placeholder="Session description/agenda..."
                    className="wkd-agenda-input"
                    rows={2}
                  />
                </div>
              ))}
              <button 
                className="wkd-add-session-btn"
                onClick={() => handleAddSession(dayIndex)}
              >
                <FiPlus /> Add Session
              </button>
            </div>
          </div>
        ))}
      </motion.div>
    );
  }

  // View Mode
  return (
    <motion.div className="wkd-schedule" variants={tabContentVariants} initial="initial" animate="animate" exit="exit">
      {canEdit && (
        <div className="wkd-schedule-header">
          <button className="wkd-edit-schedule-btn" onClick={handleEditClick}>
            <FiEdit2 /> Edit Schedule
          </button>
        </div>
      )}
      {workshop.schedule.map((day, dayIndex) => (
        <motion.div
          key={dayIndex}
          className="wkd-schedule-day"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: dayIndex * 0.1 }}
        >
          <div className="wkd-day-header">
            <span className="wkd-day-badge">Day {day.day}</span>
            <span className="wkd-day-date">{formatDate(day.date)}</span>
          </div>
          <div className="wkd-sessions">
            {day.sessions?.map((session, sessionIndex) => (
              <motion.div
                key={sessionIndex}
                className="wkd-session"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: sessionIndex * 0.05 }}
              >
                <div className="wkd-session-time">
                  <FiClock />
                  <span>{session.startTime} - {session.endTime}</span>
                </div>
                <div className="wkd-session-content">
                  <h4>{session.topic}</h4>
                  {session.agenda && <p>{session.agenda}</p>}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
};

// Loading State Component
const LoadingState = () => (
  <div className="wkd-loading">
    <div className="wkd-loading-spinner" />
    <span>Loading workshop...</span>
  </div>
);

// Error State Component
const ErrorState = ({ error, onRetry }) => (
  <div className="wkd-error-state">
    <div className="wkd-error-icon">!</div>
    <h3>Something went wrong</h3>
    <p>{error}</p>
    <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={onRetry} className="wkd-retry-btn">
      Try Again
    </motion.button>
  </div>
);

// Not Found State Component
const NotFoundState = ({ onBack }) => (
  <div className="wkd-error-state">
    <div className="wkd-error-icon">?</div>
    <h3>Workshop not found</h3>
    <p>This workshop may have been removed or doesn't exist.</p>
    <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={onBack} className="wkd-retry-btn">
      Back to Workshops
    </motion.button>
  </div>
);

// =========================================
// MAIN COMPONENT
// =========================================
const WorkshopDetail = ({ workshopId: propWorkshopId, onJoinWorkshop }) => {
  const params = useParams();
  // Support both prop-based (from PersistentWorkshopsLayout) and route-based workshopId
  const workshopId = propWorkshopId || params.workshopId;
  const navigate = useNavigate();
  const { user: currentUser } = useUserContext();
  const [workshop, setWorkshop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [joining, setJoining] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [userRole, setUserRole] = useState("visitor");
  const [isLeavingCoOrganizer, setIsLeavingCoOrganizer] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  // Co-organizer permissions (from API)
  const coOrganizerPermissions = useMemo(() => {
    if (userRole !== "co-organizer" || !workshop?.userRole?.coOrganizerPermissions) {
      return null;
    }
    return workshop.userRole.coOrganizerPermissions;
  }, [userRole, workshop]);

  // Check if user can perform specific actions based on permissions
  const canApproveParticipants = useMemo(() => {
    if (userRole === "organizer") return true;
    if (userRole === "co-organizer" && coOrganizerPermissions) {
      return coOrganizerPermissions.canApproveParticipants === true;
    }
    return false;
  }, [userRole, coOrganizerPermissions]);

  const canUploadResources = useMemo(() => {
    if (userRole === "organizer") return true;
    if (userRole === "co-organizer" && coOrganizerPermissions) {
      return coOrganizerPermissions.canUploadResources === true;
    }
    return false;
  }, [userRole, coOrganizerPermissions]);

  const canSendAnnouncements = useMemo(() => {
    if (userRole === "organizer") return true;
    if (userRole === "co-organizer" && coOrganizerPermissions) {
      return coOrganizerPermissions.canSendAnnouncements === true;
    }
    return false;
  }, [userRole, coOrganizerPermissions]);

  const canManageSchedule = useMemo(() => {
    if (userRole === "organizer") return true;
    if (userRole === "co-organizer" && coOrganizerPermissions) {
      return coOrganizerPermissions.canManageSchedule === true;
    }
    return false;
  }, [userRole, coOrganizerPermissions]);

  useEffect(() => {
    fetchWorkshopDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workshopId]);

  useEffect(() => {
    if (workshop && currentUser) {
      determineUserRole();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workshop, currentUser]);

  const fetchWorkshopDetail = async () => {
    try {
      setLoading(true);
      const response = await workshopApi.getWorkshopById(workshopId);
      if (response.success) {
        setWorkshop(response.data.workshop);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const determineUserRole = () => {
    if (!workshop) return;

    // 🔥 PRIORITY: Use userRole from API if available (backend already computed this)
    if (workshop.userRole) {
      if (workshop.userRole.isOrganizer) {
        setUserRole("organizer");
        return;
      }
      if (workshop.userRole.isCoOrganizer) {
        setUserRole("co-organizer");
        return;
      }
      if (workshop.userRole.isParticipant) {
        setUserRole("participant");
        return;
      }
      // Use relationship from API if available
      if (workshop.userRole.relationship) {
        setUserRole(workshop.userRole.relationship);
        return;
      }
    }

    // Fallback: Manual detection if API userRole not available
    if (!currentUser) {
      setUserRole("visitor");
      return;
    }

    const currentUserId = currentUser?.id || currentUser?._id;
    const organizerId = workshop.organizer?._id || workshop.organizer?.id || workshop.organizer;

    if (organizerId && organizerId === currentUserId) {
      setUserRole("organizer");
      return;
    }

    const isCoOrganizer = workshop.coOrganizers?.some((co) => {
      const coOrganizerId = co.user?._id || co.user?.id || co.user || co._id || co.id;
      return coOrganizerId === currentUserId;
    });

    if (isCoOrganizer) {
      setUserRole("co-organizer");
      return;
    }

    const isParticipant = workshop.participants?.some((p) => {
      const participantId = p.user?._id || p.user?.id || p.user || p._id || p.id;
      return participantId === currentUserId;
    });

    if (isParticipant) {
      setUserRole("participant");
      return;
    }

    setUserRole("visitor");
  };

  const handleApply = async () => {
    try {
      setJoining(true);
      if (onJoinWorkshop) {
        const isPaid = workshop.pricing?.type === "paid";
        const result = await onJoinWorkshop(workshopId, isPaid);
        
        if (result?.redirected) {
          // Paid workshop - user was redirected to payment page
          return;
        }
        
        if (result?.success) {
          // Show appropriate message
          alert(result.message);
          
          // Optimistically update local state instead of full refetch
          setWorkshop(prev => ({
            ...prev,
            userRole: {
              ...prev.userRole,
              isParticipant: true,
              participantStatus: result.status,
              shouldShowApplyButton: false,
            },
            participantCount: (prev.participantCount || 0) + (result.isInstantJoin ? 1 : 0),
          }));
          
          // Update user role
          setUserRole(result.isInstantJoin ? "participant" : "pending");
        }
      }
    } catch (err) {
alert(err.message || "Failed to join workshop. Please try again.");
    } finally {
      setJoining(false);
    }
  };

  const handleSave = () => {
    alert("Save functionality coming soon!");
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    alert("Link copied to clipboard!");
  };

  // Handle leaving as co-organizer
  const handleLeaveAsCoOrganizer = useCallback(async () => {
    setIsLeavingCoOrganizer(true);
    try {
      const response = await workshopApi.leaveAsCoOrganizer(workshopId);
      if (response.success) {
        setShowLeaveConfirm(false);
        alert("You have left the workshop as a co-organizer.");
        navigate("/workshops");
      }
    } catch (err) {
alert(err.message || "Failed to leave workshop. Please try again.");
    } finally {
      setIsLeavingCoOrganizer(false);
    }
  }, [workshopId, navigate]);

  const tabs = useMemo(() => {
    const baseTabs = [
      { id: "overview", label: "Overview" },
      { id: "schedule", label: "Schedule" },
      { id: "announcements", label: "Announcements" },
      { id: "resources", label: "Resources" },
      { id: "discussions", label: "Discussions" },
    ];

    // Show participants tab for all members (organizers, co-organizers, AND participants)
    if (userRole === "organizer" || userRole === "co-organizer" || userRole === "participant") {
      baseTabs.push({ id: "participants", label: "Participants" });
    }

    // Show settings tab only for organizer
    if (userRole === "organizer") {
      baseTabs.push({ id: "settings", label: "Settings" });
    }

    return baseTabs;
  }, [userRole, canApproveParticipants]);

  // Loading State
  if (loading) {
    return (
      <div className="wkd-page">
        <LoadingState />
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="wkd-page">
        <ErrorState error={error} onRetry={fetchWorkshopDetail} />
      </div>
    );
  }

  // Not Found State
  if (!workshop) {
    return (
      <div className="wkd-page">
        <NotFoundState onBack={() => navigate("/workshops")} />
      </div>
    );
  }

  return (
    <motion.div className="wkd-page" variants={pageVariants} initial="initial" animate="animate" exit="exit">
      {/* Compact Header */}
      <CompactHeader
        workshop={workshop}
        userRole={userRole}
        onApply={handleApply}
        onSave={handleSave}
        onShare={handleShare}
        onBack={() => navigate("/workshops")}
        isJoining={joining}
      />

      {/* Co-Organizer Info Bar - Show permissions for co-organizers */}
      {userRole === "co-organizer" && coOrganizerPermissions && (
        <motion.div 
          className="wkd-co-organizer-bar"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="wkd-co-organizer-info">
            <FiUsers className="wkd-co-icon" />
            <span className="wkd-co-label">You're a Co-Organizer</span>
            <div className="wkd-co-permissions">
              {canApproveParticipants && <span className="wkd-co-perm">✓ Participants</span>}
              {canUploadResources && <span className="wkd-co-perm">✓ Resources</span>}
              {canSendAnnouncements && <span className="wkd-co-perm">✓ Announcements</span>}
              {canManageSchedule && <span className="wkd-co-perm">✓ Schedule</span>}
            </div>
          </div>
          <button 
            className="wkd-leave-btn"
            onClick={() => setShowLeaveConfirm(true)}
            disabled={isLeavingCoOrganizer}
          >
            <FiLogOut />
            Leave
          </button>
        </motion.div>
      )}

      {/* Tabs */}
      <TabsNavigation tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Content */}
      <main className="wkd-content">
        <AnimatePresence mode="wait">
          {activeTab === "overview" && <OverviewContent key="overview" workshop={workshop} isOrganizer={userRole === "organizer"} />}

          {activeTab === "schedule" && (
            <ScheduleContent 
              key="schedule" 
              workshop={workshop} 
              workshopId={workshopId}
              canEdit={canManageSchedule}
              onScheduleUpdate={fetchWorkshopDetail}
            />
          )}

          {activeTab === "announcements" && (
            <motion.div key="announcements" variants={tabContentVariants} initial="initial" animate="animate" exit="exit">
              <Announcements 
                workshopId={workshopId} 
                userRole={userRole}
                canSendAnnouncements={canSendAnnouncements}
              />
            </motion.div>
          )}

          {activeTab === "resources" && (
            <motion.div key="resources" variants={tabContentVariants} initial="initial" animate="animate" exit="exit">
              <ResourceLibrary 
                workshopId={workshopId} 
                userRole={userRole} 
                workshop={workshop}
                canUpload={canUploadResources}
              />
            </motion.div>
          )}

          {activeTab === "discussions" && (
            <motion.div key="discussions" variants={tabContentVariants} initial="initial" animate="animate" exit="exit">
              <DiscussionForum workshopId={workshopId} userRole={userRole} workshop={workshop} />
            </motion.div>
          )}

          {activeTab === "participants" && (userRole === "organizer" || userRole === "co-organizer" || userRole === "participant") && (
            <motion.div key="participants" variants={tabContentVariants} initial="initial" animate="animate" exit="exit">
              {canApproveParticipants ? (
                <ParticipantManagement 
                  userRole={userRole}
                  canApprove={canApproveParticipants}
                />
              ) : (
                <ParticipantList workshopId={workshopId} />
              )}
            </motion.div>
          )}

          {activeTab === "settings" && userRole === "organizer" && (
            <motion.div key="settings" variants={tabContentVariants} initial="initial" animate="animate" exit="exit">
              <div className="wkd-settings-tab">
                <CoOrganizerManagement
                  workshopId={workshopId}
                  isOrganizer={true}
                  initialCoOrganizers={workshop.coOrganizers || []}
                  onUpdate={fetchWorkshopDetail}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Leave Co-Organizer Confirmation Modal */}
      <AnimatePresence>
        {showLeaveConfirm && (
          <motion.div
            className="wkd-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !isLeavingCoOrganizer && setShowLeaveConfirm(false)}
          >
            <motion.div
              className="wkd-leave-modal"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="wkd-leave-modal-icon">
                <FiAlertCircle />
              </div>
              <h3>Leave as Co-Organizer?</h3>
              <p>
                Are you sure you want to leave <strong>"{workshop.title}"</strong> as a co-organizer?
                You will lose all management access to this workshop.
              </p>
              <div className="wkd-leave-modal-actions">
                <button
                  className="wkd-leave-confirm-btn"
                  onClick={handleLeaveAsCoOrganizer}
                  disabled={isLeavingCoOrganizer}
                >
                  {isLeavingCoOrganizer ? (
                    <>
                      <FiLoader className="wkd-spinner" />
                      Leaving...
                    </>
                  ) : (
                    "Yes, Leave"
                  )}
                </button>
                <button
                  className="wkd-leave-cancel-btn"
                  onClick={() => setShowLeaveConfirm(false)}
                  disabled={isLeavingCoOrganizer}
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default WorkshopDetail;
